import * as React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import { Button, CssBaseline, Grid, Snackbar } from "@mui/material";
import Layout from "./components/commonComponents/layout/layout";
import Header from "./components/commonComponents/header/header";
import useVersionReload from "./hooks/useVersionReload";
import Home from "./pages/home/home";
import Login from "./pages/login/login";
import GettingStarted from "./pages/gettingStarted/gettingStarted";
import AboutUs from "./pages/aboutUs/aboutUs";
import ContactUs from "./pages/contactUs/contactUs";
import HowItWorks from "./pages/howItWorks/howItWorks";
import OurBlog from "./pages/ourBlog/ourBlog";
import OurClients from "./pages/ourClients/ourClients";
import OurPartners from "./pages/ourPartners/ourPartners";
import Resetpassword from "./pages/resetPassword/resetPassword";
import Results from "./pages/results/results";
import WhatYouLearn from "./pages/whatYouLearn/whatYouLearn";
import WhyZintoro from "./pages/whyZintoro/whyZintoro";
import Footer from "./components/commonComponents/footer/footer";
import Andersons from "./pages/ourClients/andersons";
import Bergquist from "./pages/ourClients/bergquist";
import CustomerRetention from "./pages/ourBlog/customerRetention";
import GeographicMarket from "./pages/ourBlog/geographicMarket";
import IndustryAnalysis from "./pages/ourBlog/industryAnalysis";
import RevenueAnalyticsGoals from "./pages/ourBlog/revenueAnalyticsGoals";
import UnderstandPurchases from "./pages/ourBlog/understandPurchases";
import WhereYouAre from "./pages/ourBlog/whereYouAre";
import BusinessRetention from "./pages/ourBlog/businessRetention";
import RevenueGoals from "./pages/ourBlog/revenueGoals";
import DashboardView from "./components/viewComponents/dashboardView";
import ProtectedRoute from "./components/reusableComponents/protectedRoute/protectedRoute";
import { useAppSelector } from "./hooks/hooks";
import ResetPasswordVerification from "./pages/resetPasswordVerification/resetPasswordVerification";
import SystemManagementView from "./components/viewComponents/systemManagementView/systemManagementView";
import TwoFactor from "./pages/twoFactor/twoFactor";
import { tokens } from "./theme";
// LEGACY — delete these two imports, IS_LEGACY and the three ternaries below.
import LegacyLogin from "./components/commonComponents/appShell/legacy/legacyLogin";
import LegacyTwoFactor from "./components/commonComponents/appShell/legacy/legacyTwoFactor";
import LegacyAuthHeader from "./components/commonComponents/appShell/legacy/legacyAuthHeader";
/**
 * Screens that bring their own frame via <AuthLayout>. They are not marketing
 * pages, so the marketing header and footer would just box in a card that is
 * already a complete screen.
 */
// Constant for the app's lifetime, so this is a module-level switch rather
// than a conditional inside a component.
const IS_LEGACY = tokens.shell === "legacy";

const AUTH_ROUTES = [
  "/login",
  "/twofactor",
  "/resetpassword",
  "/resetpasswordverification",
];

const isAuthRoute = (pathname: string) =>
  AUTH_ROUTES.some((route) => pathname.toLowerCase().startsWith(route));

function App() {
  const { loggedIn } = useAppSelector((state: any) => state.authentication);
  const { updateAvailable, reloadPage } = useVersionReload();
  const { pathname } = useLocation();

  // Marketing chrome shows only on the marketing pages: not once signed in
  // (the rail replaces it) and not on the auth screens.
  // Legacy kept the marketing header and footer on the auth screens; the new
  // shells give those screens their own frame via AuthLayout.
  const showMarketingChrome = !loggedIn && (IS_LEGACY || !isAuthRoute(pathname));
  // Legacy's header.tsx always rendered something. Signed in on an auth route
  // it was the navy greeting bar, so put that back rather than nothing.
  const showLegacyAuthHeader = IS_LEGACY && loggedIn && isAuthRoute(pathname);

  React.useEffect(() => {
    if (updateAvailable) {
      reloadPage();
    }
  }, [updateAvailable, reloadPage]);

  return (
    <React.Fragment>
      <CssBaseline />
      {/* No minWidth: the portal is responsive from here down. The legacy
          marketing pages have not had their own responsive pass yet, so they
          scroll horizontally on a phone rather than being forced to 1400px. */}
      <Grid>
        {showMarketingChrome && <Header />}
        {showLegacyAuthHeader && <LegacyAuthHeader />}
        <Grid>
          <Routes>
            <Route path="/" element={<Layout children={""} />}>
              <Route index element={<Home />} />
              <Route path="/login" element={IS_LEGACY ? <LegacyLogin /> : <Login />} />
              <Route path="/gettingstarted" element={<GettingStarted />} />
              <Route path="/aboutus" element={<AboutUs />} />
              <Route path="/contactus" element={<ContactUs />} />
              <Route path="/howitworks" element={<HowItWorks />} />
              <Route path="/ourblog" element={<OurBlog />} />
              <Route
                path="/ourblog/customerretention"
                element={<CustomerRetention />}
              />
              <Route
                path="/ourblog/geographicmarket"
                element={<GeographicMarket />}
              />
              <Route
                path="/ourblog/industryanalysis"
                element={<IndustryAnalysis />}
              />
              <Route
                path="/ourblog/revenueanalyticsgoals"
                element={<RevenueAnalyticsGoals />}
              />
              <Route
                path="/ourblog/understandpurchases"
                element={<UnderstandPurchases />}
              />
              <Route
                path="/ourblog/businessretention"
                element={<BusinessRetention />}
              />
              <Route path="/ourblog/revenuegoals" element={<RevenueGoals />} />
              <Route path="/ourblog/whereyouare" element={<WhereYouAre />} />
              <Route path="/ourclients" element={<OurClients />} />
              <Route path="/ourclients/andersons" element={<Andersons />} />
              <Route path="/ourclients/bergquist" element={<Bergquist />} />
              <Route path="/ourpartners" element={<OurPartners />} />
              <Route path="/resetpassword" element={<Resetpassword />} />
              <Route path="/whatyoulearn" element={<WhatYouLearn />} />
              <Route path="/results" element={<Results />} />
              <Route path="/whyzintoro" element={<WhyZintoro />} />
              <Route
                path="/resetpasswordverification/:token"
                element={<ResetPasswordVerification />}
              />
              <Route path="/twoFactor" element={IS_LEGACY ? <LegacyTwoFactor /> : <TwoFactor />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/dashboard" element={<DashboardView />} />
              </Route>
              <Route element={<ProtectedRoute />}>
                <Route
                  path="/systemManagement"
                  element={<SystemManagementView />}
                />
              </Route>
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
          </Routes>
        </Grid>
        {showMarketingChrome && <Footer />}
      </Grid>
      {/* <Snackbar
        open={updateAvailable}
        message="New version available"
        action={
          <Button color="secondary" size="small" onClick={reloadPage}>
            Reload
          </Button>
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      /> */}
    </React.Fragment>
  );
}

export default App;
